import { SearchIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from './button';
import { cn } from './cn';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-bg-accent-strong-primary font-medium text-text-secondary',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border-primary transition-colors hover:bg-bg-quaternary/50 data-[state=selected]:bg-bg-quaternary',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & { isCompact?: boolean }
>(({ className, isCompact = false, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 p-4 text-left align-middle font-medium text-text-tertiary [&:has([role=checkbox])]:pr-0',
      isCompact && 'p-2 first:pl-4',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { isCompact?: boolean }
>(({ className, isCompact = false, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'p-4 align-middle [&:has([role=checkbox])]:pr-0',
      isCompact && 'p-2 first:pl-4',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-text-tertiary', className)}
    {...props}
  />
));

TableCaption.displayName = 'TableCaption';

interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const TableSearch: React.FC<TableSearchProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  return (
    <div className="flex w-full items-center border-b border-border-primary">
      <SearchIcon className="size-10 p-2.5 text-text-secondary" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-none bg-transparent focus:outline-none focus:ring-0"
        placeholder={placeholder ?? 'Search...'}
      />

      {value && (
        <Button
          size="icon"
          onClick={() => onChange('')}
          variant="ghost"
          className="m-0 hover:bg-text-secondary/5"
        >
          <XIcon className="size-5 text-text-secondary" />
        </Button>
      )}
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableSearch,
};
